'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import { Input } from '@ahafood/ui/components/input';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { useDebouncedValue } from '@ahafood/ui/hooks/use-debounced-value';
import { EmptySearch } from '@ahamove/icons/empty';
import {
  CurrentLocation,
  Address as MapAddress,
  Path,
} from '@ahamove/icons/map';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, MapIcon, Search, X } from 'lucide-react';
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import MapPickerSheet from './map/map-picker-sheet';
import type { MapCoords } from './map/map-view';
import { getBatchGeocodeFn } from '@/api/ahafood/get-batch-geocode-fn';
import { useAutocomplete } from '@/api/use-autocomplete';
import { useGetUserLocations } from '@/api/use-get-user-locations';
import { getAuthToken } from '@/lib/auth/get-token';
import {
  LOCATION_UNRESOLVED_MESSAGE,
  NOT_SERVICEABLE_MESSAGE,
  resolveCityFromCoordinates,
} from '@/lib/fast-location-utils';
import { useAuth } from '@/providers/auth-provider';
import { useAiFoodOrderStore, useLocationStore } from '@/store';
import { locationMismatchStore } from '@/store/use-location-mismatch-store';

const FormSchema = z.object({
  keyword: z.string().default(''),
});

const AddressSuggestion = memo(
  ({
    suggestion,
    onSelect,
  }: {
    suggestion: Address;
    onSelect: (address: Address) => void;
  }) => (
    <Card
      className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full cursor-pointer"
      onClick={() => onSelect(suggestion)}
      data-testid="location-address-suggestion-item"
    >
      <MapAddress className="size-6 flex-none" />
      <CardHeader className="p-0 w-full">
        <CardTitle className="line-clamp-1">
          {suggestion.address.split(',')[0]}
        </CardTitle>
        <CardDescription className="line-clamp-2">
          {suggestion.address.split(',').slice(1).join(',')}
        </CardDescription>
      </CardHeader>
    </Card>
  )
);

AddressSuggestion.displayName = 'AddressSuggestion';

const SavedAddressItem = memo(
  ({
    userLocation,
    onSelect,
  }: {
    userLocation: {
      lat: number;
      lng: number;
      place_id: string;
      address: string;
      save_at: number;
    };
    onSelect: (address: Address) => void;
  }) => {
    const handleSelect = () => {
      const address: Address = {
        lat: userLocation.lat,
        lng: userLocation.lng,
        place_id: userLocation.place_id,
        address: userLocation.address,
      };
      onSelect(address);
    };

    return (
      <Card
        className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full cursor-pointer"
        onClick={handleSelect}
      >
        <MapAddress className="size-6 flex-none text-primary-50" />
        <CardHeader className="p-0 w-full">
          <CardTitle className="truncate">
            {userLocation.address.split(',')[0]}
          </CardTitle>
          <CardDescription className="line-clamp-2">
            {userLocation.address.split(',').slice(1).join(',')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
);

SavedAddressItem.displayName = 'SavedAddressItem';

const AddressSuggestionSkeleton = () => (
  <Card className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full">
    <Skeleton className="size-6 flex-none rounded-md" />
    <div className="p-0 w-full max-w-[calc(100%-2.5rem)] flex flex-col gap-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </Card>
);

const SavedAddressSkeleton = () => (
  <div className="mt-4 rounded-md h-[calc(100svh-68px-48px-66px)]">
    <div className="mb-3 px-0">
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="space-y-0">
      {[...Array(3)].map((_, index) => (
        <Fragment key={index}>
          {index > 0 && <Separator className="ml-14 w-[calc(100%-3.5rem)]" />}
          <Card className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full">
            <Skeleton className="size-6 flex-none rounded-md" />
            <div className="p-0 w-full max-w-[calc(100%-2.5rem)] flex flex-col gap-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </Card>
        </Fragment>
      ))}
    </div>
  </div>
);

export type StoreLocationContentProps = {
  address: Address | null;
  onAddressChange: (address: Address) => void;
  onClose: () => void;
  /**
   * When true, the input's "x" button only clears the search text (keeping the
   * currently selected address) so the user can type a new one. Defaults to
   * false, where clearing also resets the selected address.
   */
  clearInputOnly?: boolean;
  session?: {
    store_name?: string;
    store_image_url?: string;
  } | null;
  isMobile?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function StoreLocation({
  address,
  onAddressChange,
  onClose,
  clearInputOnly = false,
  session,
  isMobile = false,
}: StoreLocationContentProps) {
  const queryClient = useQueryClient();
  const setSkipAutoLocation = useAiFoodOrderStore.use.setSkipAutoLocation();
  const { isAuthenticated } = useAuth();
  const systemToken = getAuthToken();

  const { data: userLocationsData, isLoading: isLoadingLocation } =
    useGetUserLocations(systemToken, {
      enabled: !!systemToken,
    });

  const [addressError, setAddressError] = useState<string | null>(null);
  const detectLocation = useLocationStore.use.detectLocation();
  const loadingLocation = useLocationStore.use.loading();
  const setAddress = useAiFoodOrderStore((state) => state.setAddress);
  const [loadingAddressDetail, setLoadingAddressDetail] = useState(false);
  const [preventAutocomplete, setPreventAutocomplete] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  // Tâm map: ưu tiên địa chỉ đang chọn (fine-tune) → else để sheet fallback
  // city center; user có thể bấm FAB về GPS trong map.
  const mapInitialCenter: MapCoords | undefined =
    address?.lat && address?.lng
      ? { longitude: address.lng, latitude: address.lat }
      : undefined;
  const [selectedCurrentLocation, setSelectedCurrentLocation] =
    useState<Address | null>(null);

  // Địa chỉ đến từ URL nhưng bị từ chối (ngoài vùng phục vụ / không phân giải
  // được). Dùng để mở dialog với đúng địa chỉ đó trong ô nhập + lỗi ngay dưới,
  // để user biết CHÍNH XÁC địa chỉ nào không dùng được mà tự đổi.
  const pendingLocation = useAiFoodOrderStore.use.pendingLocation();

  // Ref chứ không phải state: prefill GPS và seed pendingLocation nằm ở hai
  // effect chạy CÙNG một commit, state set ở effect trên chưa hiện ra ở effect
  // dưới. Ref cập nhật đồng bộ nên thứ tự ưu tiên mới đúng.
  const gpsPrefilledRef = useRef(false);
  const seededPendingLabelRef = useRef<string | null>(null);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      // pendingLocation TRƯỚC address: khi dialog bung ra là để báo "địa chỉ vừa
      // nhận không dùng được", ô nhập phải là CHÍNH địa chỉ đó. Để address (địa
      // chỉ cũ trong storage) thắng thì user đọc được một thông báo lỗi gắn với
      // một địa chỉ hoàn toàn khác — tưởng địa chỉ cũ của mình vừa hỏng.
      keyword: pendingLocation?.label ?? address?.address ?? '',
    },
    mode: 'onChange',
  });

  const keywordValue = form.watch('keyword') ?? '';
  const [debouncedKeyword] = useDebouncedValue(keywordValue, 500);
  // Tracks a manual "x" clear so the effect below doesn't immediately
  // repopulate the input from the selected address.
  const [userClearedInput, setUserClearedInput] = useState(false);

  const { data: addressOptions, isLoading: loading } = useAutocomplete(
    { input: debouncedKeyword },
    {
      enabled: Boolean(
        debouncedKeyword && debouncedKeyword.length >= 2 && !preventAutocomplete
      ),
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    // Địa chỉ URL bị từ chối đang giữ ô nhập → không được lấp lại bằng địa chỉ cũ.
    if (pendingLocation) return;
    if (address?.address && !keywordValue && !userClearedInput) {
      form.setValue('keyword', address.address);
    }
  }, [address, form, keywordValue, userClearedInput, pendingLocation]);

  // "Bấm nhắc → mở chọn địa chỉ với vị trí hiện tại điền sẵn": consume địa chỉ GPS
  // đã reverse-geocode (từ popover nhắc nhỏ) làm ứng viên chờ xác nhận. Dùng 1 lần.
  useEffect(() => {
    const prefill = locationMismatchStore.getState().consumePendingPrefill();
    if (prefill?.lat && prefill?.lng) {
      gpsPrefilledRef.current = true;
      setSelectedCurrentLocation(prefill);
      setPreventAutocomplete(true);
      form.setValue('keyword', prefill.address || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Địa chỉ đến từ URL nhưng BỊ TỪ CHỐI (ngoài vùng phục vụ / không phân giải
  // được) → hiện lại nguyên văn trong ô nhập + báo lỗi ngay dưới.
  //
  // Khác `pendingPrefill` ở effect trên: kia là địa chỉ GPS DÙNG ĐƯỢC, user chủ
  // động chọn; còn đây là địa chỉ KHÔNG dùng được, hiện ra để user biết cái nào
  // hỏng mà đổi. Hai nguồn không bao giờ xảy ra cùng lúc trong thực tế, nhưng
  // nếu có thì GPS thắng — nên chỗ này đọc giá trị SỐNG của form thay vì
  // `keywordValue` của render hiện tại (effect trên chạy cùng commit, giá trị
  // trong biến vẫn còn rỗng nên sẽ đè nhầm).
  useEffect(() => {
    if (!pendingLocation) return;

    setAddressError(
      pendingLocation.reason === 'not_serviceable'
        ? NOT_SERVICEABLE_MESSAGE
        : LOCATION_UNRESOLVED_MESSAGE
    );

    // Seed một lần cho mỗi label bị từ chối. Trước đây điều kiện là "ô nhập còn
    // rỗng", nhưng defaultValues đã lấp sẵn địa chỉ cũ nên nhánh này không bao
    // giờ chạy — đúng cái bug user thấy: báo lỗi city chưa hỗ trợ mà ô nhập lại
    // là địa chỉ cũ trong storage.
    if (
      pendingLocation.label &&
      seededPendingLabelRef.current !== pendingLocation.label &&
      !gpsPrefilledRef.current &&
      !userClearedInput
    ) {
      seededPendingLabelRef.current = pendingLocation.label;
      form.setValue('keyword', pendingLocation.label);
      // Địa chỉ này đã biết là không dùng được → đừng tự bung gợi ý cho nó.
      setPreventAutocomplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLocation, form, userClearedInput]);

  useEffect(() => {
    if (
      debouncedKeyword &&
      debouncedKeyword.length >= 2 &&
      !preventAutocomplete
    ) {
      void queryClient.invalidateQueries({
        queryKey: ['autocomplete', { input: debouncedKeyword }],
      });
    }
  }, [debouncedKeyword, preventAutocomplete, queryClient]);

  const processAddressWithCoordinates = useCallback(
    async (address: Address) => {
      try {
        form.setValue('keyword', address?.address || '');

        // Verify serviceability WHILE the dialog is still open. If the address
        // is outside every served city, show an inline error under the input
        // and keep the dialog open so the user can pick another — no need to
        // reopen it. (by-point is cached, so the parent's onAddressChange
        // re-resolve hits the cache.)
        if (address?.lat && address?.lng) {
          setLoadingAddressDetail(true);
          const city = await resolveCityFromCoordinates(
            address.lat,
            address.lng
          );
          setLoadingAddressDetail(false);
          if (!city) {
            setAddressError(NOT_SERVICEABLE_MESSAGE);
            return;
          }
        }

        // Delegate ALL store mutations (setCity via by-point + setAddress +
        // navigation + query invalidation) to the parent via onAddressChange.
        onAddressChange(address);
        setSkipAutoLocation(false);
        setAddressError(null);
        onClose();
      } catch (error) {
        setLoadingAddressDetail(false);
        console.error('❌ StoreLocation: Error processing address:', error);
        setAddressError(
          'Đã có lỗi khi xử lý địa chỉ của bạn, vui lòng thử lại sau'
        );
      }
    },
    [form, onAddressChange, setSkipAutoLocation, onClose]
  );

  const handleAddressSelect = useCallback(
    async (selectedAddress: Address) => {
      setPreventAutocomplete(true);

      // If address already has coordinates, process directly
      if (selectedAddress.lat && selectedAddress.lng) {
        await processAddressWithCoordinates(selectedAddress);
        return;
      }

      // If no coordinates but has place_id, geocode the address text to coords.
      if (selectedAddress.place_id) {
        setLoadingAddressDetail(true);
        form.setValue('keyword', selectedAddress.address || '');

        try {
          const res = await getBatchGeocodeFn(selectedAddress.address);

          if (res) {
            await processAddressWithCoordinates(res);
          } else {
            setAddressError(
              'Không tìm được vị trí tương ứng với địa chỉ bạn chọn.'
            );
          }
        } catch (error) {
          console.error('❌ StoreLocation: Error geocoding address:', error);
          toast.error(
            'Đã có lỗi khi chọn địa chỉ của bạn, vui lòng thử lại sau'
          );
        } finally {
          setLoadingAddressDetail(false);
        }
        return;
      }

      // Fallback: address without coordinates or place_id
      await processAddressWithCoordinates(selectedAddress);
    },
    [form, processAddressWithCoordinates]
  );

  const handleUseCurrentLocation = useCallback(() => {
    setPreventAutocomplete(true);

    let isCompleted = false;

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        if (!isCompleted) {
          useLocationStore.setState({
            loading: false,
            error: new Error('Timeout'),
          });
          reject(new Error('Timeout: Không thể xác định vị trí trong 5 giây'));
        }
      }, 5000);
    });

    Promise.race([detectLocation(), timeoutPromise])
      .then((response) => {
        isCompleted = true;
        setSelectedCurrentLocation(response);
        form.setValue('keyword', response.address || '');
      })
      .catch((error) => {
        isCompleted = true;
        console.error(error);
        setPreventAutocomplete(false);
        setSelectedCurrentLocation(null);
        useLocationStore.setState({ loading: false });
        toast.error('Vui lòng cho phép bật vị trí trên trình duyệt này');
      });
  }, [detectLocation, form]);

  const handleConfirmLocation = useCallback(async () => {
    if (!selectedCurrentLocation) {
      setAddressError(
        'Không thể xác nhận địa chỉ. Vui lòng chọn hoặc nhập lại.'
      );
      return;
    }

    // Serviceability check before closing (parity with picking a suggestion).
    if (selectedCurrentLocation.lat && selectedCurrentLocation.lng) {
      setLoadingAddressDetail(true);
      const city = await resolveCityFromCoordinates(
        selectedCurrentLocation.lat,
        selectedCurrentLocation.lng
      );
      setLoadingAddressDetail(false);
      if (!city) {
        setAddressError(NOT_SERVICEABLE_MESSAGE);
        return;
      }
    }

    onAddressChange(selectedCurrentLocation);
    setAddressError(null);
    // Reset skip auto location flag when user confirms current location
    setSkipAutoLocation(false);
    onClose();
  }, [selectedCurrentLocation, onAddressChange, onClose, setSkipAutoLocation]);

  const handleClearInput = useCallback(() => {
    // Clear only the search text so the user can type a new address; keep the
    // input empty until they type (userClearedInput) and re-enable autocomplete.
    form.setValue('keyword', '');
    setPreventAutocomplete(false);
    setAddressError(null);
    setSelectedCurrentLocation(null);
    setLoadingAddressDetail(false);
    setUserClearedInput(true);

    if (!clearInputOnly) {
      // Default mode also drops the selected address.
      setAddress(null);
      // Set flag to skip auto location when user manually clears address
      setSkipAutoLocation(true);
    }
  }, [form, setAddress, setSkipAutoLocation, clearInputOnly]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue('keyword', e.target.value);
      setPreventAutocomplete(false);
      setSelectedCurrentLocation(null);
      setUserClearedInput(false);
      setAddressError(null);
    },
    [form]
  );

  const handleFormChange = useCallback(() => {
    setPreventAutocomplete(false);
  }, []);

  const totalLoading = loading || loadingLocation || loadingAddressDetail;

  const shouldShowSuggestions = Boolean(
    !loadingAddressDetail &&
      !preventAutocomplete &&
      !totalLoading &&
      debouncedKeyword &&
      addressOptions &&
      addressOptions.length > 0
  );

  const shouldShowEmptyResults = Boolean(
    !totalLoading &&
      debouncedKeyword &&
      addressOptions &&
      addressOptions.length === 0
  );

  return (
    <>
      {/* Store Info Card - Only show if session data is available */}
      {session && (
        <Card className="overflow-hidden relative border-none rounded-none shadow-none">
          <Avatar className="h-32 w-full rounded-none flex-none">
            <AvatarImage
              src={session?.store_image_url ?? '/placeholder.svg'}
              alt={session?.store_name}
              className="object-top object-cover"
            />
            <AvatarFallback className="rounded-none">
              {session?.store_name}
            </AvatarFallback>
          </Avatar>
          <CardHeader className="p-4 w-full">
            <CardTitle className="line-clamp-2">
              {session?.store_name}
            </CardTitle>
          </CardHeader>
        </Card>
      )}

      {/* Address Input Card */}
      <Card
        className="overflow-hidden relative border-none rounded-none shadow-none"
        data-testid="location-page"
      >
        <CardHeader className="p-4 w-full pb-8 bg-white">
          <div className="flex flex-row flex-nowrap justify-start items-center truncate gap-2 w-full">
            <Path className="size-5 flex-none" />
            <CardTitle data-testid="location-title">
              Bạn ơi giao món đến đâu?
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
          <div className="relative">
            <form
              className="w-full"
              onChange={handleFormChange}
              onSubmit={(e) => {
                e.preventDefault();
                void handleConfirmLocation();
              }}
            >
              <div className="relative flex items-center">
                <Search
                  className="absolute left-3 size-5 text-neutral-40 pointer-events-none"
                  data-testid="location-address-search"
                />
                <Input
                  placeholder="Nhập địa chỉ giao hàng của bạn"
                  className={cn(
                    'h-12 w-full truncate rounded-xl border-neutral-20 bg-neutral-10/30 px-10 text-sm font-bold hover:bg-neutral-30/30',
                    addressError &&
                      'border-destructive ring-2 ring-destructive/30 focus-visible:ring-destructive hover:bg-neutral-10/30'
                  )}
                  value={keywordValue}
                  onChange={handleInputChange}
                  aria-invalid={!!addressError}
                  data-testid="location-address-input"
                />
                <div className="absolute right-3 flex items-center justify-center h-full pointer-events-none">
                  {totalLoading ? (
                    <Loader2 className="size-5 animate-spin text-neutral-40" />
                  ) : keywordValue ? (
                    <Button
                      size="sm"
                      className="size-6 p-0 pointer-events-auto rounded-full bg-neutral-40 hover:bg-neutral-50"
                      onClick={handleClearInput}
                      aria-label="Clear input"
                      type="button"
                      data-testid="location-address-input-clear-button"
                    >
                      <X className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            </form>

            {addressError && (
              <p
                className="mt-2 text-sm font-medium text-destructive"
                data-testid="location-address-error"
              >
                {addressError}
              </p>
            )}

            {totalLoading && (
              <div className="mt-4">
                {[...Array(3)].map((_, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <Separator className="ml-14 w-[calc(100%-3.5rem)]" />
                    )}
                    <AddressSuggestionSkeleton />
                  </Fragment>
                ))}
              </div>
            )}

            {shouldShowSuggestions && (
              <ScrollArea
                className="mt-4 md:h-[calc(650px-76px-16px-46px-16px-env(safe-area-inset-bottom))] h-[calc(100svh-68px-48px-16px-env(safe-area-inset-bottom))]"
                data-testid="location-address-suggestion-list"
              >
                {addressOptions?.map((suggestion, index) => (
                  <Fragment key={suggestion.place_id ?? index}>
                    {index > 0 && <Separator className="ml-14" />}
                    <AddressSuggestion
                      suggestion={suggestion}
                      onSelect={handleAddressSelect}
                    />
                  </Fragment>
                ))}
                {/* Lối thoát khi gợi ý không có địa chỉ mong muốn */}
                <Separator className="ml-14" />
                <Card
                  className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-center border-none rounded-none shadow-none w-full cursor-pointer"
                  onClick={() => setMapOpen(true)}
                  data-testid="location-suggestion-pick-on-map"
                >
                  <div className="flex size-10 flex-none items-center justify-center rounded-full bg-primary-10">
                    <MapIcon className="size-5 text-primary-50" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">
                      Không thấy địa chỉ của bạn?
                    </p>
                    <p className="text-xs text-neutral-50">
                      Chọn vị trí trực tiếp trên bản đồ
                    </p>
                  </div>
                </Card>
              </ScrollArea>
            )}

            {shouldShowEmptyResults ? (
              <div className={cn('mt-4', isMobile ? 'mx-[-16px]' : '')}>
                <Card className="flex flex-col px-4 py-6 items-center border-none rounded-none shadow-none w-full">
                  <EmptySearch className="size-20 mb-4" />
                  <CardTitle className="text-center">
                    Không tìm thấy địa chỉ
                  </CardTitle>
                  <CardDescription className="text-center mt-2">
                    Không tìm thấy địa chỉ phù hợp với từ khóa tìm kiếm.
                    <br />
                    Vui lòng thử lại với từ khóa khác.
                  </CardDescription>
                  <Button
                    variant="link"
                    size="lg"
                    className="w-full mt-4 bg-primary-10 [&_svg]:size-6 gap-3 hover:no-underline"
                    onClick={() => setMapOpen(true)}
                    type="button"
                    data-testid="location-empty-pick-on-map"
                  >
                    <MapIcon className="flex-none text-primary-50" />
                    Chọn vị trí trên bản đồ
                  </Button>
                </Card>
              </div>
            ) : null}

            {!shouldShowSuggestions &&
              !shouldShowEmptyResults &&
              !totalLoading && (
                <div>
                  {isAuthenticated && isLoadingLocation && (
                    <SavedAddressSkeleton />
                  )}
                  <CardFooter className="py-4 px-0 flex-col gap-3">
                    <div className="relative w-full my-2.5">
                      <Separator className="w-full" />
                      <Badge
                        variant="secondary"
                        className="bg-white text-neutral-40 absolute -top-2 left-1/2 -translate-x-1/2 z-[1]"
                      >
                        Hoặc
                      </Badge>
                    </div>
                    <Button
                      variant="link"
                      size="lg"
                      className="w-full bg-primary-10 [&_svg]:size-6 gap-3 hover:no-underline"
                      onClick={handleUseCurrentLocation}
                      disabled={loadingLocation}
                      type="button"
                      data-testid="location-use-current-location-button"
                    >
                      <CurrentLocation className="flex-none [&_rect]:fill-white [&_path]:fill-primary-50" />
                      {loadingLocation
                        ? 'Đang tìm vị trí...'
                        : 'Sử dụng vị trí hiện tại'}
                    </Button>
                    <Button
                      variant="link"
                      size="lg"
                      className="w-full bg-neutral-10 text-neutral-70 [&_svg]:size-6 gap-3 hover:no-underline"
                      onClick={() => setMapOpen(true)}
                      type="button"
                      data-testid="location-pick-on-map-button"
                    >
                      <MapIcon className="flex-none text-primary-50" />
                      {address?.lat && address?.lng
                        ? 'Ghim chính xác trên bản đồ'
                        : 'Chọn trên bản đồ'}
                    </Button>
                  </CardFooter>

                  {isAuthenticated &&
                    !isLoadingLocation &&
                    userLocationsData?.address_histories &&
                    userLocationsData.address_histories.length > 0 &&
                    !selectedCurrentLocation && (
                      <div className="mt-4 rounded-md">
                        <div className="mb-3 px-0">
                          <h3 className="text-sm font-semibold text-neutral-60">
                            Địa chỉ đã lưu
                          </h3>
                        </div>
                        <ScrollArea
                          className={cn(
                            selectedCurrentLocation
                              ? 'md:h-[calc(330px-16px)] h-[calc(100svh-68px-48px-66px-64px-160px)]'
                              : 'md:h-[calc(330px)] h-[calc(100svh-68px-48px-66px-64px-100px)]'
                          )}
                        >
                          {userLocationsData.address_histories.map(
                            (userLocation, index) => (
                              <Fragment
                                key={`${userLocation.save_at}-${index}`}
                              >
                                {index > 0 && <Separator className="ml-14" />}
                                <SavedAddressItem
                                  userLocation={userLocation}
                                  onSelect={handleAddressSelect}
                                />
                              </Fragment>
                            )
                          )}
                        </ScrollArea>
                      </div>
                    )}
                  {selectedCurrentLocation && (
                    <Button
                      type="button"
                      variant="default"
                      size="lg"
                      className="w-full font-bold mt-3 sticky bottom-[env(safe-area-inset-bottom)]"
                      onClick={handleConfirmLocation}
                    >
                      Xác nhận địa chỉ
                    </Button>
                  )}
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      <MapPickerSheet
        open={mapOpen}
        onOpenChange={setMapOpen}
        initialCenter={mapInitialCenter}
        initialAddress={address ?? null}
        onConfirm={handleAddressSelect}
      />
    </>
  );
}
